import { useEffect, useMemo, useState } from "react";

import { buildWorkspaceChatContext } from "../ai/workspaceContext";
import { useNexusOptional } from "../ai/NexusProvider";
import CommandCenterPanel from "./CommandCenterPanel";

import { isAdminDashboardPath } from "./assistantPageContext";
import { IconSearch, IconSparkles } from "./assistantUi";

export function AdminAssistantSearchBar({
  onActivate,
  onSubmit,
  routeLabel,
  active = false,
}) {
  const [value, setValue] = useState("");
  const placeholder = routeLabel
    ? `Query NEXUS Engine · ${routeLabel}`
    : "Query NEXUS AI Engine — insights, flows, actions...";

  const handleActivate = () => {
    onActivate?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const query = value.trim();
    if (query) {
      onSubmit?.(query);
      setValue("");
    } else {
      onActivate?.();
    }
  };

  const isMac = typeof window !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  return (
    <form
      onSubmit={handleSubmit}
      onClick={handleActivate}
      className={`admin-topnav-search group flex-1 min-w-0 flex items-center gap-2.5 rounded-xl border px-3.5 py-1.5 transition-all duration-300 cursor-text ${
        active ? "admin-topnav-search--active" : ""
      }`}
    >
      <IconSearch className="admin-topnav-search-icon w-4 h-4 shrink-0 transition-colors" />
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={handleActivate}
        placeholder={placeholder}
        className="admin-topnav-search-input flex-1 min-w-0 bg-transparent border-0 p-0 text-xs sm:text-sm focus:outline-none focus:ring-0 cursor-text"
        aria-label="Ask NEXUS"
      />
      <button
        type="submit"
        onClick={(e) => e.stopPropagation()}
        className="admin-topnav-search-run hidden sm:inline-flex items-center gap-1 shrink-0 text-[10px] font-semibold rounded-md px-2 py-0.5 transition-colors"
        title="Submit (Enter)"
      >
        <IconSparkles className="admin-topnav-search-run-icon w-3 h-3" />
        Run
      </button>
    </form>
  );
}

export default function AdminAssistantDock({
  pathname,
  context,
  disabled = false,
  open,
  onOpenChange,
}) {
  const nexus = useNexusOptional();
  const pendingPrompt = nexus?.pendingPrompt || "";
  const setPendingPrompt = nexus?.setPendingPrompt;

  const routeLabel = useMemo(() => {
    if (isAdminDashboardPath(pathname)) return "NEXUS Command";
    if (pathname.includes("/portfolio")) return "Portfolio Intelligence";
    if (pathname.includes("/engagement")) return "Engagement";
    if (pathname.includes("/studio")) return "Workspace Studio";
    if (pathname.includes("/branding")) return "Workspace branding";
    if (pathname.includes("/email-templates")) return "Mail Manager";
    if (pathname.includes("/analytics")) return "Portfolio · Operations";
    if (pathname.includes("/insights")) return "Portfolio · Performance";
    if (pathname.includes("/invites")) return "Engagement · Pipeline";
    if (pathname.includes("/invite")) return "Engagement · Compose";
    if (pathname.includes("/company-profile")) return "Studio · Companies";
    if (pathname.includes("/report-profiles")) return "Studio · Reports";
    if (pathname.includes("/assessment-form")) return "Studio · Survey";
    return "Admin";
  }, [pathname]);

  const effectiveContext = useMemo(() => {
    const base = context || {
      page: isAdminDashboardPath(pathname)
        ? "dashboard"
        : pathname.includes("/portfolio")
          ? pathname.includes("lens=performance")
            ? "insights"
            : "analytics"
          : pathname.includes("/engagement")
            ? pathname.includes("lens=pipeline")
              ? "invites"
              : "invite"
            : pathname.includes("/branding")
              ? "workspace_branding"
              : pathname.includes("/email-templates")
                ? "mail_manager"
                : pathname.includes("/studio")
                  ? pathname.includes("lens=survey")
                    ? "assessment_form"
                    : pathname.includes("lens=reports")
                      ? "report_profiles"
                      : "company_profile"
                  : pathname.includes("/company-profile")
                    ? "company_profile"
                    : pathname.includes("/report-profiles")
                      ? "report_profiles"
                      : pathname.includes("/assessment-form")
                        ? "assessment_form"
                        : pathname.includes("/analytics")
                          ? "analytics"
                          : pathname.includes("/insights")
                            ? "insights"
                            : pathname.includes("/invites")
                              ? "invites"
                              : pathname.includes("/invite")
                                ? "invite"
                                : "admin",
      scope: { label: routeLabel, text: "Current admin page context" },
      pagePath: pathname,
      surface: "command_center",
      currentRoute: pathname,
    };
    return buildWorkspaceChatContext(base, pathname);
  }, [context, routeLabel, pathname]);

  useEffect(() => {
    setPendingPrompt?.("");
  }, [pathname, setPendingPrompt]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  return (
    <>
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[1px]"
          aria-label="Close command center"
          onClick={() => onOpenChange(false)}
        />
      ) : null}

      <div
        className={`admin-assistant-dock-panel absolute left-0 right-0 top-full z-50 origin-top border-b border-slate-200/90 bg-slate-50 shadow-2xl transition-all duration-300 ease-out ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto max-h-[min(78vh,720px)]"
            : "opacity-0 -translate-y-3 pointer-events-none max-h-0 overflow-hidden"
        }`}
        role="dialog"
        aria-modal={open}
        aria-hidden={!open}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col max-h-[min(78vh,720px)] min-h-0">
          <div className="shrink-0 px-4 sm:px-5 py-3 bg-gradient-to-r from-[#004f73] via-[#005a82] to-[#0a7ea4] text-white flex items-center justify-between gap-3">
            <div className="min-w-0 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 shrink-0">
                <IconSparkles className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-semibold tracking-tight leading-tight">NEXUS command center</h2>
                <p className="text-xs text-white/75 truncate">Context-aware · {routeLabel}</p>
              </div>
            </div>
            <button
              type="button"
              className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xl leading-none flex items-center justify-center transition-colors shrink-0"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 py-4 sm:py-5">
            <CommandCenterPanel
              context={effectiveContext}
              pathname={pathname}
              disabled={disabled}
              showInsights
              usePlatformChat
              registerWorkflow={!isAdminDashboardPath(pathname)}
              initialPrompt={pendingPrompt}
            />
          </div>
        </div>
      </div>
    </>
  );
}
